import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, Link2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/admin/AdminLayout";

export const ComparisonGenerator = () => {
  const qc = useQueryClient();
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const deals = useQuery({ queryKey: ["a-deals-published"], queryFn: () => api.get("/admin/deals?status=published&limit=100").then((r) => r.data.items) });
  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < 4 ? [...cur, id] : cur));
  const go = async () => {
    setBusy(true);
    const t = toast.loading("Writing comparison draft…");
    try { await api.post("/admin/ai/comparison", { deal_ids: ids }); toast.success("Comparison draft ready in the list above", { id: t }); setIds([]); qc.invalidateQueries({ queryKey: ["ai-guides"] }); }
    catch (e) { toast.error(errMsg(e), { id: t }); } finally { setBusy(false); }
  };
  return (
    <Panel title="AI comparison pages" testid="comparison-generator-panel">
      <p className="text-xs text-slate-500 mb-3">Pick 2-4 published deals. The AI compares only the facts we hold and marks missing specs - it never invents ratings. The draft appears in the AI drafts list above for review before publishing.</p>
      <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
        {(deals.data || []).map((d) => (
          <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={ids.includes(d.id)} onChange={() => toggle(d.id)} data-testid={`compare-pick-${d.id}`} />
            <span className="truncate">{d.title}</span><span className="ml-auto text-xs text-slate-500 whitespace-nowrap">{d.merchant_name}</span>
          </label>
        ))}
      </div>
      <button disabled={ids.length < 2 || busy} onClick={go} data-testid="comparison-generate-button" className="mt-3 h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold disabled:opacity-50 flex items-center gap-2">
        <Sparkles size={16} /> {busy ? "Writing…" : `Draft comparison (${ids.length} selected)`}
      </button>
    </Panel>
  );
};

export const CategoryIntroGenerator = () => {
  const qc = useQueryClient();
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const cats = useQuery({ queryKey: ["a-categories"], queryFn: () => api.get("/admin/categories").then((r) => r.data) });
  const drafts = useQuery({ queryKey: ["cat-intros"], queryFn: () => api.get("/admin/ai/category-intros").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["cat-intros"] });
  const go = async () => {
    setBusy(true);
    const t = toast.loading("Writing category introduction…");
    try { await api.post(`/admin/ai/category-intro/${cat}`); toast.success("Draft ready below", { id: t }); refresh(); }
    catch (e) { toast.error(errMsg(e), { id: t }); } finally { setBusy(false); }
  };
  return (
    <Panel title="AI category introductions & FAQs" testid="category-intro-panel">
      <p className="text-xs text-slate-500 mb-3">Writes a short intro, meta description and 3 FAQs for a category page. Nothing changes on the site until you approve it.</p>
      <div className="flex flex-wrap gap-2">
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="category-intro-select">
          <option value="">Choose category…</option>
          {(cats.data || []).map((c) => <option key={c.id} value={c.id}>{c.parent_id ? "— " : ""}{c.name}</option>)}
        </select>
        <button disabled={!cat || busy} onClick={go} data-testid="category-intro-generate-button" className="h-10 px-4 rounded-lg bg-amber-500 text-white font-bold disabled:opacity-50 flex items-center gap-2"><Sparkles size={16} /> {busy ? "Writing…" : "Draft intro"}</button>
      </div>
      {(drafts.data || []).map((c) => <IntroDraft key={`${c.id}-${c.ai_intro_draft.created_at}`} c={c} refresh={refresh} />)}
    </Panel>
  );
};

const IntroDraft = ({ c, refresh }) => {
  const d = c.ai_intro_draft;
  const [intro, setIntro] = useState(d.intro);
  const [meta, setMeta] = useState(d.meta_description);
  const approve = async () => { try { await api.post(`/admin/ai/category-intro/${c.id}/approve`, { intro, meta_description: meta }); toast.success("Category page updated"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const reject = async () => { await api.delete(`/admin/ai/category-intro/${c.id}`); refresh(); };
  return (
    <div className="mt-4 border border-slate-200 rounded-lg p-4 space-y-2" data-testid={`category-intro-draft-${c.slug}`}>
      <p className="font-semibold">{c.name}</p>
      <textarea rows={4} value={intro} onChange={(e) => setIntro(e.target.value)} className="w-full rounded-md border border-slate-200 p-2 text-sm" data-testid="category-intro-text" />
      <input value={meta} maxLength={160} onChange={(e) => setMeta(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" placeholder="Meta description" />
      {d.faqs?.map((f) => <p key={f.q} className="text-xs text-slate-600"><b>Q:</b> {f.q} <b>A:</b> {f.a}</p>)}
      {d.missing_info?.length > 0 && <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2">Missing information: {d.missing_info.join(", ")}</p>}
      <div className="flex justify-end gap-2">
        <button onClick={reject} className="px-3 py-1.5 rounded-lg border text-sm" data-testid="category-intro-reject">Discard</button>
        <button onClick={approve} className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white font-bold text-sm" data-testid="category-intro-approve">Approve & publish</button>
      </div>
    </div>
  );
};

export const LinkIdeas = ({ postId }) => {
  const [items, setItems] = useState(null);
  const load = async () => { try { setItems((await api.get(`/admin/posts/${postId}/link-suggestions`)).data); } catch (e) { toast.error(errMsg(e)); } };
  if (!items) return <button onClick={load} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-1" data-testid="link-ideas-button"><Link2 size={14} /> Internal link ideas</button>;
  return (
    <div className="w-full text-xs bg-slate-50 rounded p-3" data-testid="link-ideas-list">
      <p className="font-semibold mb-1">Add these internal links when editing (Catalog → Guides & blog):</p>
      {items.length ? items.map((l) => <p key={l.url}>{l.type}: <b>{l.anchor}</b> → <code>{l.url}</code></p>) : <p>No matching pages found.</p>}
    </div>
  );
};
